import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LogIn } from 'lucide-react';
import { API, inputCls } from '@/lib/api';
import { LogoMark } from '@/components/Logo';
import { useLanguage, TOKEN_KEY } from '@/context/LanguageContext';

export default function Login() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [creds, setCreds] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!creds.username || !creds.password) { toast.error(t('common.required')); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email: creds.username, password: creds.password });
      if (['admin', 'editor'].includes(data.user?.role)) {
        localStorage.setItem(TOKEN_KEY, data.token);
        navigate('/admin');
      } else {
        toast.success(t('login.welcome'));
        navigate('/');
      }
    } catch { toast.error(t('login.error')); } finally { setLoading(false); }
  };

  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-brand-paper px-5 pt-28 pb-16" data-testid="login-page">
      <div className="pointer-events-none absolute inset-0 grid-bg opacity-70" />
      <div className="pointer-events-none absolute -top-24 start-[15%] h-96 w-96 rounded-full bg-brand-sky/20 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 end-[10%] h-96 w-96 rounded-full bg-brand-teal/15 blur-3xl" />
      <div className="pointer-events-none absolute top-1/2 start-[55%] h-72 w-72 rounded-full bg-brand-violet/10 blur-3xl" />

      <form onSubmit={submit} className="relative w-full max-w-md rounded-[2rem] border border-brand-line bg-white/90 p-9 shadow-[0_50px_100px_-40px_rgba(15,23,42,0.35)] glass" data-testid="login-form">
        <div className="text-center">
          <img src={t('settings.logo')} alt="WiyakGPS" className="mx-auto h-20 w-20 rounded-full shadow-[0_20px_40px_-16px_rgba(0,82,255,0.5)]" data-testid="login-logo" />
          <h1 className="mt-5 font-display text-2xl font-extrabold text-brand-ink" data-testid="login-title">{t('login.title')}</h1>
          <p className="mt-1.5 text-sm text-brand-steel">{t('login.sub')}</p>
        </div>

        <div className="mt-7 space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold text-brand-steel">{t('login.username')}</span>
            <input data-testid="login-username-input" dir="ltr" value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })} className={inputCls} autoComplete="username" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-bold text-brand-steel">{t('login.password')}</span>
            <input data-testid="login-password-input" type="password" dir="ltr" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} className={inputCls} autoComplete="current-password" />
          </label>
        </div>

        <button type="submit" disabled={loading} data-testid="login-submit-btn" className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-brand-blue py-3.5 text-sm font-bold text-white transition-all duration-300 hover:bg-brand-blueHover hover:shadow-[0_16px_36px_-12px_rgba(0,82,255,0.6)] disabled:opacity-60">
          <LogIn className="h-4 w-4" /> {loading ? t('common.submitting') : t('login.btn')}
        </button>

        <div className="mt-5 flex items-center justify-between text-xs">
          <button type="button" data-testid="login-forgot-link" onClick={() => toast.info(t('login.forgotMsg'))} className="font-bold text-brand-blue hover:underline">
            {t('login.forgot')}
          </button>
          <Link to="/" className="font-bold text-brand-steel hover:text-brand-blue">{t('nav.home')}</Link>
        </div>
        <p className="mt-6 rounded-xl bg-brand-alt px-4 py-3 text-center font-mono text-[10px] text-brand-dim" dir="ltr" data-testid="login-demo-note">{t('login.adminNote')}</p>
      </form>
    </div>
  );
}
